// src/pages/ProcessingRecipeDetail.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Badge } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import config from "../../config";
import Layout from "../../components/layouts/Main";

const ProcessingRecipeDetail = () => {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("processingStages");
  const [currentBatches, setCurrentBatches] = useState([]);
  const [pastBatches, setPastBatches] = useState([]);
  const [outputAverages, setOutputAverages] = useState({});

  const fetchAllStored = async (batches) => {
    const storedPromises = batches.map(async (batch) => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/production-batches/${batch._id}/stored-products`);
        return { batchId: batch._id, data: res.data };
      } catch (err) {
        console.error(`Error fetching stored for batch ${batch._id}`, err);
        return { batchId: batch._id, data: { products: [], totalEntries: 0 } };
      }
    });
    const storedResults = await Promise.all(storedPromises);
    const storedMap = {};
    storedResults.forEach(res => {
      storedMap[res.batchId] = res.data;
    });
    return storedMap;
  };

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/production-batches?bomId=${id}`);
      const batches = res.data;
      const current = batches.filter(b => ["pending", "in-progress", "paused"].includes(b.status));
      const past = batches
        .filter((b) => b.status === "completed")
        .sort(
          (a, b) =>
            new Date(b.completedAt || b.updatedAt) -
            new Date(a.completedAt || a.updatedAt)
        );

      const allBatches = [...current, ...past];
      if (allBatches.length > 0) {
        const storedData = await fetchAllStored(allBatches);
        const enrichBatch = (batch) => {
          const stored = storedData[batch._id] || { products: [], totalEntries: 0 };
          return { ...batch, storedProducts: stored };
        };
        const enrichedCurrent = current.map(enrichBatch);
        const enrichedPast = past.map(enrichBatch);
        setCurrentBatches(enrichedCurrent);
        setPastBatches(enrichedPast);

        // Calculate averages for last 5 past batches
        const last5Past = enrichedPast.slice(0, 5);
        const averages = {};

        if (last5Past.length > 0) {
          const productStats = {}; // { productId: { sumPct: 0, count: 0 } }

          last5Past.forEach((batch) => {
            const totalInput = batch.inputs.reduce(
              (sum, input) => sum + (input.quantity || 0),
              0
            );
            if (totalInput > 0) {
              batch.storedProducts.products.forEach((p) => {
                const pid = p.product._id;
                if (!productStats[pid]) {
                  productStats[pid] = { sumPct: 0, count: 0 };
                }
                const actualPct = (p.totalStored / totalInput) * 100;
                productStats[pid].sumPct += actualPct;
                productStats[pid].count += 1;
              });
            }
          });

          Object.keys(productStats).forEach((pid) => {
            averages[pid] = (
              productStats[pid].sumPct / productStats[pid].count
            ).toFixed(2);
          });
        }
        setOutputAverages(averages);
      }
    } catch (err) {
      console.error("Error fetching batches", err);
    }
  }, [id]);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/process-definitions/${id}`);
        setRecipe(res.data);
        // Fetch batches after recipe is loaded
        await fetchBatches();
      } catch (err) {
        console.error("Error loading recipe or batches", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id, fetchBatches]);

  const getBatchOutputData = (batch) => {
    return batch.outputs.map(output => {
      const productId = output.productId._id;
      const storedProduct = batch.storedProducts.products.find(p => p.product._id === productId);
      const storedQty = storedProduct ? storedProduct.totalStored : 0;
      const calculatedQty = storedProduct ? storedProduct.totalCalculated : (output.calculatedOutput || 0);
      const diffPercent = calculatedQty > 0 ? ((storedQty - calculatedQty) / calculatedQty) * 100 : 0;
      return {
        productName: output.productId.productName,
        percentage: output.percentage,
        stored: storedQty,
        calculated: calculatedQty,
        diffPercent: diffPercent.toFixed(2),
        unit: storedProduct ? storedProduct.unit : output.unit
      };
    });
  };

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/boms">BOM</Link></li>
          <li className="breadcrumb-item active">{recipe?.name}</li>
        </ol>
      </nav>

      {loading ? (
        <PulseLoader
          color="#014b7cbb"
          size={9}
        />
      ) : (
        <div className="pb-9">
          <div className="row">
            <div className="col-12">
              <div className="row align-items-center justify-content-between g-3 mb-1">
                <div className="col-auto">
                  <h2 className="mb-0">{recipe?.name}</h2>
                </div>
                <div className="col-auto">
                  <div className="d-flex align-items-center">
                    <div>
                      <Link
                        className="btn px-3 btn-sm btn-outline-secondary"
                        to={`/boms/edit/${recipe?._id}`}
                      >
                        Edit
                      </Link>
                    </div>
                    {/* <div>
                      <button
                        className="btn px-3 btn-fms-secondary"
                        type="button"
                        data-bs-toggle="dropdown"
                        data-boundary="window"
                        aria-haspopup="true"
                        aria-expanded="false"
                        data-bs-reference="parent"
                      >
                        <HiMenuAlt1 className="fs-6" />
                      </button>
                      <ul
                        className="dropdown-menu dropdown-menu-end p-0"
                        style={{ zIndex: 9999 }}
                      >
                        <li>
                          <Link className="dropdown-item text-danger" to="#!">
                            Delete BOM
                          </Link>
                        </li>
                      </ul>
                    </div> */}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <p className="text-body-secondary fs-85 mb-4">{recipe?.description}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Sidebar */}
            <div className="col-md-5 col-lg-4">
              <div className="sticky-top" style={{ top: "5rem" }}>
                {recipe && (
                  <>
                    {/* Bom Id */}
                    <div className="card mb-3">
                      <div className="card-body">
                        <ul className="list-group list-group-flush text-info  ">
                          {recipe.bomId}
                        </ul>
                      </div>
                    </div>
                    {/* Raw Materials */}
                    <div className="card mb-3">
                      <div className="card-body">
                        <h5 className="fw-bolder mb-3">Raw Materials</h5>
                        <ul className="list-group list-group-flush">
                          {recipe.rawMaterials.map((rm, idx) => (
                            <li key={idx} className="list-group-item d-flex justify-content-between px-2">
                              <span>{rm.materialId.productName}</span>
                              <span>{rm.quantity} {rm.unit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Outputs */}
                    <div className="card mb-3">
                      <div className="card-body">
                        <h5 className="fw-bolder mb-3">Outputs</h5>
                        <ul className="list-group list-group-flush">
                          {[...recipe.outputs]
                            .sort((a, b) => b.percentage - a.percentage)
                            .map((out, idx) => {
                              const avg = outputAverages[out.productId._id];
                              return (
                                <li
                                  key={idx}
                                  className="list-group-item d-flex justify-content-between px-2"
                                >
                                  <span>{out.productId.productName}</span>
                                  <span className="text-end">
                                    <span className="d-block">{out.percentage}%</span>
                                    {avg && (
                                      <small className="d-block text-success fw-normal" style={{ fontSize: '0.75rem' }}>
                                        Last 5 Avg: {avg}%
                                      </small>
                                    )}
                                    {out.projectedOutput
                                      ? ` | ${out.projectedOutput} ${out.productId.unitId.unitCode}/hr`
                                      : ""}
                                  </span>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Main Content with Scrollspy */}
            <div className="col-md-7 col-lg-8">
              <div className="lead-details-container">
                {/* Tab Nav */}
                <ul className="nav nav-underline fs-9 mb-4">
                  <li className="nav-item">
                    <button
                      className={`nav-link me-2 ${activeTab === "processingStages" ? "active" : ""}`}
                      onClick={() => setActiveTab("processingStages")}
                    >
                      Processing Stages
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link me-2 ${activeTab === "currentProductions" ? "active" : ""}`}
                      onClick={() => setActiveTab("currentProductions")}
                    >
                      Current Running Batches
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "pastProductions" ? "active" : ""}`}
                      onClick={() => setActiveTab("pastProductions")}
                    >
                      Past Batches
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {loading && <p>Loading...</p>}
                  {!loading && !recipe && <p className="text-danger">Recipe not found.</p>}

                  {recipe && (
                    <>
                      {activeTab === "processingStages" && (
                        <div className="tab-pane fade show active mb-8">
                          <h5 className="mb-4 fw-bolder">Processing Stages</h5>
                          {recipe.processingStages.map((stage, idx) => (
                            <div key={idx} className="border rounded-3 p-3 mb-3">
                              <h5 className="mb-2">{stage.stageName}</h5>
                              <p className="mb-0 text-body-secondary d-flex gap-3 flex-wrap fs-85">
                                <span><strong>Machine:</strong> {stage.machine?.name || 'N/A'}</span> <span className="opacity-50">|</span>
                                <span><strong> Gain/Loss:</strong> {stage.gainLoss ?? 'N/A'} %</span> <span className="opacity-50">|</span>
                                <span><strong> Capacity/hr:</strong> {stage.capacityPerHour ?? 'N/A'} {stage.capacityUnit}</span>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {activeTab === "currentProductions" && (
                        <div className="tab-pane fade show active mb-8">
                          <h5 className="mb-4 fw-bolder">Current Production Running Batches</h5>
                          {currentBatches.length > 0 ? currentBatches.map(batch => (
                            <div key={batch._id} className="border rounded-3 p-3 mb-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0">{batch.batchNumber}</h6>
                                <Badge bg={batch.status === 'completed' ? 'success' : batch.status === 'in-progress' ? 'warning' : 'secondary'}>{batch.status}</Badge>
                              </div>
                              <div className="mb-2">
                                {getBatchOutputData(batch).map((out, idx) => (
                                  <div key={idx} className="text-sm">
                                    ({out.percentage}%)<strong>{out.productName}:</strong> {" "}
                                    {out.stored} / {out.calculated} {out.unit}{" "}
                                    (
                                    <span
                                      className={
                                        out.diffPercent > 0
                                          ? "text-success"
                                          : out.diffPercent < 0
                                            ? "text-danger"
                                            : "text-muted"
                                      }
                                    >
                                      {out.diffPercent}%
                                    </span>
                                    )
                                  </div>
                                ))}
                              </div>
                              <Link to={`/batch/${batch._id}`} className="btn btn-outline-primary btn-sm">Show Details</Link>
                            </div>
                          )) : <p className="text-body-secondary">No current runs.</p>}
                        </div>
                      )}

                      {activeTab === "pastProductions" && (
                        <div className="tab-pane fade show active mb-8">
                          <h5 className="mb-4 fw-bolder">Past Production Batches</h5>
                          {pastBatches.length > 0 ? pastBatches.map(batch => (
                            <div key={batch._id} className="border rounded-3 p-3 mb-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0">{batch.batchNumber}</h6>
                                <Badge bg="success">{batch.status}</Badge>
                              </div>
                              <div className="mb-2">
                                {getBatchOutputData(batch).map((out, idx) => (
                                  <div key={idx} className="text-sm">
                                    <strong>{out.productName}:</strong> {out.stored} / {out.calculated} {out.unit} ({out.diffPercent}%)
                                  </div>
                                ))}
                              </div>
                              <Link to={`/batch/${batch._id}`} className="btn btn-outline-primary btn-sm">Show Details</Link>
                            </div>
                          )) : <p className="text-body-secondary">No past runs.</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProcessingRecipeDetail;
